import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { updateMyDecor } from '../../../redux/apis'

import { changeS3Url } from '../../../utils/fetchUtils'
import useOnClickOutside from '../../../hooks/useOnClickOutside'

import { ReactComponent as CoinIcon } from '../../../assets/coin.svg'
import { ReactComponent as LockIcon } from '../../../assets/lock.svg'
import Portal from '../../DesignerCommon/portal'
import { fetchFeed } from '../../../redux/feedInfiniteSlice'
import { FEED_RESET } from '../../../redux/constants'
import useItem from '../../../hooks/useItem'
import { ReactComponent as CheckIcon } from '../../../assets/checkcircle.svg'
import FilterSelections from '../tray/filter/FilterSelections'
import { APP_CACHE, ASSET_URL } from '../../../constants/ApiEndpoints'

const categories = [
  ...require('../../../redux/data/categories.json'),
  { shortName: 'Others', name: 'others', iconName: 'others1.svg', children: [] }
]

const Root = styled.div`
  position: relative;
  height: ${({ size }) => (size === 'large' ? '100%' : 'auto')};
`

const Container = styled.div`
  position: relative;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
  width: ${({ size }) => (size === 'large' ? '100%' : '136px')};
  height: ${({ size }) => (size === 'large' ? '100%' : '172px')};
  text-align: center;
  background-color: ${props => props.theme.background.secondary};
  user-select: none;
  opacity: ${props => (props.disabled ? '0.2' : '1.0')};
  box-sizing: border-box;
`

const Border = styled.div`
  border: 2px solid #ff5e6d;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  border-radius: 10px;
`

const Image = styled.img`
  margin-top: 12px;
  min-width: 100px;
  width: ${({ size }) => (size === 'large' ? '90%' : '105px')};
  height: ${({ size }) => (size === 'large' ? '210px' : '105px')};
  object-fit: contain;

  @media screen and (max-width: 750px) {
    width: ${({ size }) => (size === 'large' ? '90%' : '105px')};
    height: ${({ size }) => (size === 'large' ? 'calc(50vw - 60px)' : '105px')};
  }
`

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 10px;
  padding: 0;
  position: absolute;
  height: ${({ size }) => (size === 'large' ? '62px' : '36px')};
  bottom: 0px;
  left: 0px;
  right: 0px;
  color: ${props => props.theme.text.primary};
  border-top: 1px solid ${props => props.theme.card.primary};

  @media screen and (max-width: 750px) {
    height: 36px;
  }
`

const CoinContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
  font-weight: 500;
  font-size: 10px;
  width: ${({ size }) => (size === 'large' ? '124px' : '72px')};
  height: ${({ size }) => (size === 'large' ? '35px' : '20px')};
  background-color: #f8f8f8;
  border-radius: ${({ size }) => (size === 'large' ? '150px' : '10px')};
  margin: 8px;
  color: black;

  @media screen and (max-width: 750px) {
    width: 72px;
    height: 20px;
    border-radius: 20px;
  }
`

const Coin = styled(CoinIcon)`
  width: 14px;
  height: 14px;
  padding: 0;
  padding-right: 2px;
  padding-left: 4px;
  margin: 0;
`

const MemberTitle = styled.span`
  font-size: ${({ size }) => (size === 'large' ? '13px' : '9px')};

  @media screen and (max-width: 750px) {
    font-size: 9px;
  }
`

const MemberBadge = styled.img`
  width: ${({ size }) => (size === 'large' ? '25px' : '16px')};
  height: ${({ size }) => (size === 'large' ? '25px' : '16px')};
  padding-right: ${({ size }) => (size === 'large' ? '5px' : '2px')};
  margin-left: ${({ size }) => (size === 'large' ? '10px' : '4px')};

  @media screen and (max-width: 750px) {
    width: 16px;
    height: 16px;
    padding-right: 2px;
    margin-left: 4px;
  }
`

const LockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.28);

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  font-size: 20px;
  font-weight: 500;
  color: white;
`

const Lock = styled(LockIcon)`
  width: 25px;
`

const MoreOptionsButton = styled.button`
  background-color: transparent;
  border: none;
  width: ${({ size }) => (size === 'large' ? '40px' : '25px')};
  height: ${({ size }) => (size === 'large' ? '40px' : '25px')};
  position: relative;
  cursor: pointer;
  top: 17px;
  right: ${({ size }) => (size === 'large' ? '-80px' : '-45px')};
  border-radius: 50%;
  transition: 0.2s;

  @media screen and (max-width: 750px) {
    width: 25px;
    height: 25px;
    right: ${({ size }) => (size === 'large' ? '-35%' : '-45px')};
    top: ${({ size }) => (size === 'large' ? '12px' : '17px')};
  }

  &:hover {
    background: #f5f5f5;
  }
`

const MoreOptionsIcon = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 3px;
  height: 3px;
  background-color: black;
  border-radius: 50%;
  transform: translate(-50%, -50%);

  &:before,
  &:after {
    left: -6px;
    content: '';
    width: 3px;
    height: 3px;
    background-color: black;
    border-radius: 50%;
    position: absolute;
  }

  &:after {
    left: auto;
    right: -6px;
  }
`

const DropDownContainer = styled.div`
  position: absolute;
  width: 118px;
  border-radius: 5px;
  background-color: white;
  box-shadow: 0px 4px 4px 0px #0000001a;
  overflow: hidden;
  z-index: 10;
  left: 50%;
  transform: translate(-50%, -12px);
`

const DropDownItem = styled.button`
  width: 100%;
  height: 43px;
  border: none;
  background-color: transparent;
  font-family: Helvetica Neue;
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  letter-spacing: 0px;
  text-align: left;
  color: #ff5e6d;
  transition: all 0.2s;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid #e5e5e5;
  }

  &:hover {
    background-color: #ff5e6d;
    color: white;
  }
`

const CategoriesContainer = styled.div`
  position: absolute;
  width: 580px;
  z-index: 50;
  border-radius: 16px;
  background: #f5f5f5;
  box-shadow: 0px 4px 10px 0px #00000026;
  padding: 25px 20px;

  @media screen and (max-width: 750px) {
    width: min(580px, calc(100vw - 20px));
    box-sizing: border-box;
    left: 10px !important;
    top: 65px !important;
    height: auto;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
  }
`

const CategoriesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
`

const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  width: 30px;
  height: 30px;
  position: relative;
  cursor: pointer;

  &:before,
  &:after {
    width: 2px;
    background: #828282;
    border-radius: 2px;
    height: 35px;
    content: '';
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &:after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`

const CategoriesList = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const CategoriesItem = styled.button`
  border: none;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: left;
  height: 40px;
  width: 135px;
  padding: 0 10px;
  background-color: white;
  border-radius: 40px;
  margin-right: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  color: #000;
  border: 2px solid ${({ isSelected }) => (isSelected ? '#ff5e6d' : '#fff')};

  & img {
    margin-right: 5px;
    height: 24px;
    width: 24px;
  }

  @media screen and (max-width: 750px) {
    margin-right: 5px;
    width: calc(50% - 5px);
  }
`

const CategoriesSubmit = styled.button`
  height: 36px;
  width: 144px;
  border-radius: 37px;
  color: #fff;
  display: flex;
  align-items: center;
  background: #ff5e6d;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  margin: auto;
  cursor: pointer;

  &:disabled {
    background: #c4c4c4;
  }
`

const NotificationIcon = styled(CheckIcon)`
  width: 18px;
  height: 18px;
  stroke-width: 2;
  margin-right: 10px;
`

const NotificationCatalog = styled.div`
  width: 136px;
  margin: 0 5px;
  cursor: default;
  pointer-events: none;
`

const MyDecorCard = ({
  refFn,
  data,
  numCoins,
  locked,
  limitInfo,
  onDelete,
  onLockClick,
  setNotificationChildren,
  size,
  selectCategoryForRecentlyCreated = true
}) => {
  const { createItemAndDrag, createItemAndAddTarget } = useItem()
  const { member } = useSelector(state => state.profile)
  const dispatch = useDispatch()

  useEffect(() => {
    // show categories modal if the item was created in the last 2 seconds
    if (
      selectCategoryForRecentlyCreated &&
      new Date(data.createdAt) > new Date() - 2000
    ) {
      setShowCategories(true)
    }
  }, [data.createdAt])

  const handleClick = async e => {
    const original = changeS3Url(data.cfImageUrl) + '?' + APP_CACHE
    await createItemAndAddTarget(original, data, 'mydecor')
  }

  const handleDragStart = e => {
    const original = changeS3Url(data.cfImageUrl) + '?' + APP_CACHE
    createItemAndDrag(e, original, data, 'mydecor')
  }

  const handleDragEnd = e => {
    e.currentTarget.style.opacity = 1
  }

  const handleDelete = () => {
    onDelete(data)
  }

  const [showOptions, setShowOptions] = useState(false)
  const [showCategories, setShowCategories] = useState(false)
  const moreOptionsRef = useRef(null)
  const categoriesModalRef = useRef(null)

  const toggleMoreOptions = () => {
    setShowOptions(!showOptions)
  }

  useOnClickOutside(moreOptionsRef, () => setShowOptions(false))
  useOnClickOutside(categoriesModalRef, () => setShowCategories(false))

  const toggleCategories = () => {
    setShowCategories(!showCategories)
    setShowOptions(false)
  }

  const rootRef = useRef(null)

  const [selectedCategory, setSelectedCategory] = useState(null)
  const selectItemCategory = async () => {
    setShowCategories(false)
    setSelectedCategory(null)
    await updateMyDecor({
      userProductId: data.objectId,
      category: selectedCategory
    })
    dispatch(fetchFeed('mydecor')({ status: FEED_RESET }))

    const categoryName = categories.find(
      ({ name }) => name === selectedCategory
    ).shortName
    if (setNotificationChildren)
      setNotificationChildren(
        <>
          <NotificationIcon />
          Successed move to ‘{categoryName}’, you can click{' '}
          <NotificationCatalog>
            <FilterSelections />
          </NotificationCatalog>{' '}
          to check it.
        </>
      )
  }

  return (
    <Root ref={rootRef} size={size}>
      <Container
        ref={refFn}
        draggable={false}
        isSelected={showCategories}
        size={size}
      >
        <div onClick={handleClick}>
          <Image
            src={data.cfImageUrl}
            alt="MyDecor"
            draggable={true}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            size={size}
          />
          <InfoContainer size={size}>
            <CoinContainer size={size}>
              {member === true ? (
                <>
                  <MemberBadge
                    src={ASSET_URL + 'designer/badge_membership_on.png'}
                    size={size}
                  />
                  <MemberTitle size={size}> Unlimited</MemberTitle>
                </>
              ) : (
                <>
                  <Coin />
                  <div>
                    {numCoins}
                    {locked}
                  </div>
                </>
              )}
            </CoinContainer>
          </InfoContainer>
        </div>
        <MoreOptionsButton onClick={toggleMoreOptions} size={size}>
          <MoreOptionsIcon />
        </MoreOptionsButton>

        {locked && (
          <LockOverlay
            onClick={e => {
              if (onLockClick) onLockClick()
            }}
          >
            <Lock />
            <div>+ {limitInfo}</div>
          </LockOverlay>
        )}
        {showCategories && <Border />}
      </Container>
      {showOptions && (
        <DropDownContainer ref={moreOptionsRef}>
          <DropDownItem
            onClick={() => {
              toggleCategories()
              setShowOptions(false)
            }}
          >
            Sort
          </DropDownItem>
          <DropDownItem
            onClick={() => {
              handleDelete()
              setShowOptions(false)
            }}
          >
            Delete
          </DropDownItem>
        </DropDownContainer>
      )}
      {showCategories && (
        <Portal>
          <CategoriesContainer
            style={{
              left:
                rootRef.current?.getClientRects()[0].x +
                (size === 'large'
                  ? 0
                  : rootRef.current?.getClientRects()[0].width + 5) +
                'px',
              top:
                rootRef.current?.getClientRects()[0].y +
                (size === 'large'
                  ? rootRef.current?.getClientRects()[0].height + 5
                  : 0) +
                'px'
            }}
            ref={categoriesModalRef}
          >
            <CategoriesHeader>
              Choose a Catalog..
              <CloseButton onClick={toggleCategories} />
            </CategoriesHeader>
            <CategoriesList>
              {categories.map(({ shortName, name, iconName }) => (
                <CategoriesItem
                  key={name}
                  onClick={() => setSelectedCategory(name)}
                  isSelected={name === selectedCategory}
                >
                  <img
                    src={ASSET_URL + 'designer/category/' + iconName}
                    alt={shortName}
                  />
                  {shortName}
                </CategoriesItem>
              ))}
            </CategoriesList>
            <CategoriesSubmit
              onClick={selectItemCategory}
              disabled={!selectedCategory}
            >
              Move
            </CategoriesSubmit>
          </CategoriesContainer>
        </Portal>
      )}
    </Root>
  )
}

export default MyDecorCard
